import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useLanguage, languageOptions, Language } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "next-themes";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ProfilePage = () => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { profile, user, refreshProfile } = useAuth();

  const themeOptions = [
    { value: "light", label: t("profile.theme_light"), icon: Sun },
    { value: "dark", label: t("profile.theme_dark"), icon: Moon },
    { value: "system", label: t("profile.theme_system"), icon: Monitor },
  ];

  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [saving, setSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(language);
  const [selectedTheme, setSelectedTheme] = useState(theme || "system");
  const [savingAppearance, setSavingAppearance] = useState(false);

  const handleSaveDetails = async () => {
    if (!user) return;
    setSaving(true);
    const { error } = await supabase.from("profiles").update({ first_name: firstName, last_name: lastName, email }).eq("user_id", user.id);
    setSaving(false);
    if (error) { toast.error(error.message); } else { await refreshProfile(); toast.success(t("profile.saved")); }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error(t("profile.password_mismatch")); return; }
    if (newPassword.length < 6) { toast.error(t("profile.password_short")); return; }
    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) { toast.error(error.message); } else { toast.success(t("profile.password_updated")); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }
  };

  const handleSaveAppearance = async () => {
    if (!user) return;
    setSavingAppearance(true);
    setLanguage(selectedLanguage as any);
    setTheme(selectedTheme);
    const { error } = await supabase.from("profiles").update({ language: selectedLanguage, theme: selectedTheme } as any).eq("user_id", user.id);
    setSavingAppearance(false);
    if (error) { toast.error(error.message); } else { await refreshProfile(); toast.success(t("profile.appearance_saved")); }
  };

  return (
    <div>
      <h1 className="text-xl font-bold text-foreground mb-6">{t("profile.title")}</h1>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("profile.personal")}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2"><Label>{t("profile.first_name")}</Label><Input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></div>
          <div className="space-y-2"><Label>{t("profile.last_name")}</Label><Input value={lastName} onChange={(e) => setLastName(e.target.value)} /></div>
          <div className="space-y-2 md:col-span-2"><Label>{t("profile.email")}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
        </div>
        <Button className="mt-4" onClick={handleSaveDetails} disabled={saving}>{saving ? t("profile.saving") : t("profile.save_details")}</Button>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("profile.change_password")}</h2>
        <div className="grid gap-4">
          <div className="space-y-2"><Label>{t("profile.current_password")}</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} /></div>
          <div className="space-y-2"><Label>{t("profile.new_password")}</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} /></div>
          <div className="space-y-2"><Label>{t("profile.confirm_password")}</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} /></div>
        </div>
        <Button className="mt-4" onClick={handleChangePassword} disabled={changingPassword || !newPassword}>{changingPassword ? t("profile.updating") : t("profile.update_password")}</Button>
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("profile.appearance")}</h2>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>{t("settings.language")}</Label>
            <Select value={selectedLanguage} onValueChange={(val) => setSelectedLanguage(val as Language)}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="z-50 bg-popover border border-border shadow-md">
                {languageOptions.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2"><span>{lang.flag}</span><span>{lang.label}</span></span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("profile.theme")}</Label>
            <div className="grid grid-cols-3 gap-3">
              {themeOptions.map((opt) => (
                <button key={opt.value} onClick={() => setSelectedTheme(opt.value)} className={cn("flex flex-col items-center gap-2 rounded-xl border p-4 transition-all", selectedTheme === opt.value ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-border hover:border-primary/30 hover:bg-muted/50")}>
                  <opt.icon className={cn("h-5 w-5", selectedTheme === opt.value ? "text-primary" : "text-muted-foreground")} />
                  <span className={cn("text-sm font-medium", selectedTheme === opt.value ? "text-foreground" : "text-muted-foreground")}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <Button className="mt-6" onClick={handleSaveAppearance} disabled={savingAppearance}>{savingAppearance ? t("profile.saving") : t("profile.save_appearance")}</Button>
      </section>
    </div>
  );
};

export default ProfilePage;
